import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../lib/apiClient';
import { labelFor, listOptions, useConfig } from '../context/ConfigContext';
import { useConfirm } from '../context/ConfirmContext';
import { useToast } from '../context/ToastContext';
import { formatMoney } from '../utils/format';

// TS port of the source (Falah) app's pages/InventoryDetail.jsx. Field
// edits happen inline here (toggled by `editing` state) rather than a
// separate route, matching the source app's own convention. Stock level
// itself is never part of that edit form — only the "Record a movement"
// form below changes `current_quantity`, via POST
// /inventory-items/{id}/movements (InventoryItemController::storeMovement()).
interface InventoryFormState {
  name: string;
  category: string;
  unit: string;
  reorder_point: string;
  unit_price: string;
  facility_id: string | number;
  notes: string;
}

const emptyForm: InventoryFormState = {
  name: '',
  category: '',
  unit: 'pcs',
  reorder_point: '',
  unit_price: '',
  facility_id: '',
  notes: '',
};

interface MovementFormState {
  type: string;
  quantity: string;
  reason: string;
  occurred_at: string;
}

const emptyMovement: MovementFormState = {
  type: 'received',
  quantity: '',
  reason: '',
  occurred_at: new Date().toISOString().slice(0, 10),
};

interface FacilityOption {
  id: number;
  name: string;
}

interface Movement {
  id: number;
  type: string;
  quantity_change: number;
  reason: string | null;
  occurred_at: string;
  user: { full_name: string } | null;
}

interface InventoryItemRecord {
  id: number;
  name: string;
  category: string | null;
  unit: string;
  current_quantity: number;
  reorder_point: number | null;
  unit_price: number | null;
  facility_id: number | null;
  facility: { id: number; name: string } | null;
  notes: string | null;
  movements: Movement[];
}

type FormErrors = Record<string, string[]>;

export function InventoryDetail() {
  const { lists, settings } = useConfig();
  const fmtMoney = (n: number) => formatMoney(n, settings.currency);
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<InventoryItemRecord | null>(null);
  const [facilities, setFacilities] = useState<FacilityOption[]>([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<InventoryFormState>(emptyForm);
  const [errors, setErrors] = useState<FormErrors>({});
  const [movementForm, setMovementForm] = useState<MovementFormState>(emptyMovement);
  const [movementError, setMovementError] = useState('');

  const refresh = async () => {
    const res = await api.get<InventoryItemRecord>(`/inventory-items/${id}`);
    setItem(res.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    api.get<{ data: FacilityOption[] }>('/facilities', { params: { per_page: 100 } }).then((res) => setFacilities(res.data.data));
  }, []);

  const startEdit = (current: InventoryItemRecord) => {
    setForm({
      name: current.name,
      category: current.category ?? '',
      unit: current.unit,
      reorder_point: current.reorder_point != null ? String(current.reorder_point) : '',
      unit_price: current.unit_price != null ? String(current.unit_price) : '',
      facility_id: current.facility_id ?? '',
      notes: current.notes ?? '',
    });
    setErrors({});
    setEditing(true);
  };

  const set =
    (key: keyof InventoryFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm({ ...form, [key]: e.target.value });
  const setMovement =
    (key: keyof MovementFormState) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setMovementForm({ ...movementForm, [key]: e.target.value });

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const payload: Record<string, unknown> = Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      await api.put(`/inventory-items/${id}`, payload);
      setEditing(false);
      await refresh();
    } catch (err) {
      setErrors(
        err instanceof ApiError && err.errors ? err.errors : { general: [err instanceof ApiError ? err.message : 'Save failed.'] }
      );
    }
  };

  const remove = async () => {
    if (!item) return;
    if (!(await confirmDelete(`Delete "${item.name}"?`))) return;
    try {
      await api.delete(`/inventory-items/${id}`);
      navigate('/resources/inventory');
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  const recordMovement = async (e: FormEvent) => {
    e.preventDefault();
    setMovementError('');
    try {
      await api.post(`/inventory-items/${id}/movements`, movementForm);
      setMovementForm(emptyMovement);
      await refresh();
    } catch (err) {
      setMovementError(
        err instanceof ApiError ? err.fieldError('quantity') ?? err.message : 'Could not record movement.'
      );
    }
  };

  if (!item) return null;

  return (
    <>
      <header className="page-header">
        <h1>{item.name}</h1>
        {!editing && (
          <div className="page-header-actions">
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/resources/inventory')}>
              Back to list
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => startEdit(item)}>
              Edit details
            </button>
            <button type="button" className="btn btn-danger-ghost" onClick={remove}>
              Delete item
            </button>
          </div>
        )}
      </header>

      <div className="card">
        {!editing ? (
          <div className="form-grid">
            <p className="span-2">
              <strong>
                {Number(item.current_quantity)} {labelFor(lists, 'inventory_unit', item.unit)}
              </strong>{' '}
              in stock
              {item.reorder_point !== null && <span className="muted"> · reorder at {Number(item.reorder_point)}</span>}
              {item.facility && <span className="muted"> · {item.facility.name}</span>}
            </p>
            <p className="span-2 muted">
              {item.category ? labelFor(lists, 'inventory_category', item.category) : 'No category'}
              {item.unit_price !== null && <> · {fmtMoney(item.unit_price)} each</>}
            </p>
            {item.notes && <p className="span-2">{item.notes}</p>}

            <div className="span-2">
              <h3>Record a movement</h3>
              {movementError && <div className="alert alert-error">{movementError}</div>}
              <form onSubmit={recordMovement} className="form-grid">
                <label>
                  Type
                  <select value={movementForm.type} onChange={setMovement('type')}>
                    <option value="received">Received (stock in)</option>
                    <option value="issued">Issued (used/consumed)</option>
                    <option value="adjusted">Adjustment (correction)</option>
                  </select>
                </label>
                <label>
                  Quantity {movementForm.type === 'adjusted' ? '(+/-)' : ''}
                  <input
                    type="number"
                    step="0.01"
                    value={movementForm.quantity}
                    onChange={setMovement('quantity')}
                    required
                  />
                </label>
                <label>
                  Date
                  <input type="date" value={movementForm.occurred_at} onChange={setMovement('occurred_at')} />
                </label>
                <label>
                  Reason
                  <input
                    value={movementForm.reason}
                    onChange={setMovement('reason')}
                    placeholder="e.g. Restock, monthly usage"
                  />
                </label>
                <div className="form-actions span-2">
                  <button className="btn btn-primary">Record movement</button>
                </div>
              </form>
            </div>

            <div className="span-2">
              <h3>Movement history</h3>
              <ul className="simple-list">
                {item.movements?.map((m) => (
                  <li key={m.id}>
                    <strong>{m.type}</strong> {Number(m.quantity_change) > 0 ? '+' : ''}
                    {Number(m.quantity_change)} — {m.user?.full_name}
                    {m.reason && <> ({m.reason})</>}
                    <div>
                      <small className="muted">{new Date(m.occurred_at).toLocaleDateString('en-GB')}</small>
                    </div>
                  </li>
                ))}
                {(!item.movements || item.movements.length === 0) && (
                  <li className="muted">No movements recorded yet.</li>
                )}
              </ul>
            </div>
          </div>
        ) : (
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error">{errors.general[0]}</div>}
            <label className="span-2">
              Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label>
              Category
              <select value={form.category} onChange={set('category')}>
                <option value="">—</option>
                {listOptions(lists, 'inventory_category').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Unit
              <select value={form.unit} onChange={set('unit')}>
                {listOptions(lists, 'inventory_unit').map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Reorder point
              <input type="number" step="0.01" min="0" value={form.reorder_point} onChange={set('reorder_point')} />
            </label>
            <label>
              Unit price ({settings.currency})
              <input type="number" step="0.01" min="0" value={form.unit_price} onChange={set('unit_price')} />
            </label>
            <label>
              Facility / storage location
              <select value={form.facility_id} onChange={set('facility_id')}>
                <option value="">—</option>
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="span-2">
              Notes
              <textarea rows={2} value={form.notes} onChange={set('notes')} />
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(false)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        )}
      </div>
    </>
  );
}
